import * as React from 'react';
import { ScrollArea as ScrollAreaPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={cn('relative overflow-hidden', className)}
    {...props}
  >
    {/*
      Radix ScrollAreaPrimitive.Viewport renders an internal direct-child
      <div> with ``display: table; min-width: 100%`` so its content can
      grow horizontally to enable horizontal scrolling. That table-display
      means the child sizes to its content's min-content width, NOT to the
      viewport's actual width. When the viewport is narrow (e.g. the
      352px filter sidebar) and a descendant has a min-content larger
      than 352px (a row of inline-flex chips that "fit" because the
      table gave them ~550px of phantom room), the whole content tree
      inherits that wider width and overflows the visible viewport.
      Symptom: chip strips that should ``flex-wrap`` stay on one row
      and the sidebar appears to bleed past its right edge.

      We never use horizontal scrolling via ScrollArea in this app
      (every consumer wants vertical-only scrolling), so override the
      table to ``block``. ``[&>div]`` targets only Radix's internal
      direct-child wrapper, so our own content's ``display`` is
      untouched. ``!important`` is required because Radix sets the
      display inline.
    */}
    <ScrollAreaPrimitive.Viewport className='h-full w-full rounded-[inherit] [&>div]:block!'>
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none opacity-0 transition-opacity duration-300 select-none data-[state=visible]:opacity-100',
      orientation === 'vertical' &&
        'h-full w-2.5 border-l border-l-transparent p-px',
      orientation === 'horizontal' &&
        'h-2.5 flex-col border-t border-t-transparent p-px',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className='relative flex-1 rounded-full bg-border' />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
